import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, AuthResponse } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const AdminUsers: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [users, setUsers] = useState<AuthResponse['user'][]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');

    // Edit Modal State
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<AuthResponse['user'] | null>(null);
    const [editRole, setEditRole] = useState('user');
    const [updating, setUpdating] = useState(false);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getUsers({ page, limit: 10, search });
            setUsers(response.data);
            setTotalPages(response.meta.totalPages);
        } catch (err) {
            setError(t('common.error'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchUsers();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [page, search]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleBan = async (user: AuthResponse['user']) => {
        if (!confirm(t('admin.users.confirm_ban').replace('{0}', user.username))) return; // Ensure translation exists or use fallback
        try {
            // Toggle status: if active -> ban (false), if inactive -> unban (true)
            await adminAPI.updateUserStatus(user.id, !user.isActive);
            fetchUsers();
        } catch (error) {
            console.error('Failed to update status', error);
            alert('Failed to update user status');
        }
    };

    const openEditModal = (user: AuthResponse['user']) => {
        setSelectedUser(user);
        setEditRole(user.role);
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!selectedUser) return;
        try {
            setUpdating(true);
            await adminAPI.updateUserRole(selectedUser.id, editRole);
            setIsEditModalOpen(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (error) {
            console.error('Failed to update role', error);
            alert('Failed to update user role');
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div className="container mx-auto py-8 px-4 relative">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                    {t('admin.users.title')}
                </h1>
                <button
                    onClick={() => navigate('/admin')}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                    {t('admin.back_to_dashboard')}
                </button>
            </div>

            <div className="mb-6">
                <input
                    type="text"
                    placeholder={t('admin.users.search_placeholder')}
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full md:w-1/3 bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
                />
            </div>

            {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-6">
                    {error}
                </div>
            )}

            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">{t('admin.users.table.user')}</th>
                                <th className="px-6 py-4">{t('admin.users.table.role')}</th>
                                <th className="px-6 py-4">{t('admin.users.table.status')}</th>
                                <th className="px-6 py-4">{t('admin.users.table.joined')}</th>
                                <th className="px-6 py-4">{t('admin.users.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                                        {t('admin.users.loading')}
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                                        {t('admin.users.empty')}
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-700/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold mr-3 overflow-hidden">
                                                    {user.avatarUrl ? (
                                                        <img src={user.avatarUrl} alt={user.username} className="w-full h-full object-cover" />
                                                    ) : (
                                                        (user.username[0] || '?').toUpperCase()
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-white">{user.username}</div>
                                                    <div className="text-sm text-gray-400">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-blue-900/50 text-blue-400' : 'bg-gray-700 text-gray-300'
                                                }`}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
                                                }`}>
                                                {user.isActive ? 'Aktif' : 'Pasif'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-400 text-sm">
                                            {new Date(user.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => openEditModal(user)}
                                                className="text-blue-400 hover:text-blue-300 mr-3 transition-colors"
                                            >
                                                {t('common.edit')}
                                            </button>
                                            <button
                                                onClick={() => handleBan(user)}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                {user.isActive ? t('admin.users.action.ban') : 'Unban'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-700 flex justify-between items-center">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className={`px-3 py-1 rounded ${page === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
                        >
                            {t('common.previous')}
                        </button>
                        <span className="text-gray-400">
                            {t('common.page_info').replace('{0}', page.toString()).replace('{1}', totalPages.toString())}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className={`px-3 py-1 rounded ${page === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
                        >
                            {t('common.next')}
                        </button>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {isEditModalOpen && selectedUser && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-md shadow-2xl">
                        <h2 className="text-xl font-bold text-white mb-4">{t('admin.users.edit_user') || 'Edit User'}</h2>

                        <div className="mb-4">
                            <label className="block text-gray-400 text-sm font-bold mb-2">Username</label>
                            <div className="bg-gray-700/50 rounded px-3 py-2 text-gray-300">{selectedUser.username}</div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-gray-400 text-sm font-bold mb-2">{t('admin.users.table.role')}</label>
                            <select
                                value={editRole}
                                onChange={(e) => setEditRole(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                            >
                                <option value="user">User</option>
                                <option value="moderator">Moderator</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                disabled={updating}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors disabled:opacity-50"
                            >
                                {updating ? t('common.saving') : t('common.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
